import styled, { css, keyframes } from "styled-components";
import {
    type DisplayProps,
    type MinWidthProps,
    type SpaceProps,
    type TypographyProps,
    type WidthProps,
    position,
    space,
    typography,
    width,
} from "styled-system";
import { type BreakPointValuesType, type ThemeTypedColorProps, getBreakpoint } from "../../themes/common";
import { IconAnimatedToggleArrowStyle } from "./AnimatedStyles";
import IconWrapper from "./IconWrapper";
import { theme } from "../v2/lexus/theme/lexusTheme";

// LEXUS squeezeAnimation (because animation names can change in the lexus css)
export const squeezeAnimation = keyframes`
    0% {
        transform: scaleX(1);
        transform-origin: right;
      }
    50% {
        transform: scaleX(0);
        transform-origin: right;
    }
    50.01% {
        transform: scaleX(0);
        transform-origin: left;
    }
    100% {
        transform: scaleX(1);
        transform-origin: left;
    }

`;

export type ButtonType = {
    size?: BreakPointValuesType;
    hasIcon?: boolean;
    hasIconRight?: boolean;
    cursor?: string;
    disabled?: boolean;
} & DisplayProps &
    WidthProps &
    MinWidthProps &
    SpaceProps &
    TypographyProps &
    ThemeTypedColorProps;

export const Button = styled.button<ButtonType>`
    display: inline-block;
    position: relative;
    padding: ${(props) => (props.size && props.size === "sm" ? "13px 13px 10px" : "23px 25px 20px")};
    font-family: ${theme.fonts.bold};
    font-size: 0.6875rem;
    letter-spacing: 0.1rem;
    line-height: 1.6;
    text-transform: uppercase;
    color: ${theme.colors.midnight};
    border: 1px solid ${theme.colors.platinumLight};
    border-radius: 0;
    cursor: pointer;
    ${width};
    ${space};
    ${typography};
    ${({ disabled }) =>
        disabled &&
        css`
            background-color: ${theme.colors.silver} !important;
        `};
    @media ${getBreakpoint("up", "md")} {
        padding-right: ${(props) => (props.size && props.size === "sm" ? "40px" : "65px")};
        text-align: start;
        &::after {
            position: absolute;
            top: 50%;
            right: ${theme.space[3]}px;
            width: 25px;
            height: 1px;
            content: "";
            background: ${theme.colors.black};
        }
        &:hover::after {
            animation: ${squeezeAnimation} 1s ease 0s infinite normal none;
        }
    }

    ${IconWrapper} {
        ${({ hasIconRight }) =>
            hasIconRight &&
            css`
                margin-left: 15px;
            `};
    }

    html[dir="rtl"] & {
        @media ${getBreakpoint("up", "md")} {
            padding-left: ${(props) => (props.size && props.size === "sm" ? "40px" : "65px")};
            padding-right: 0;
            &::after {
                left: ${theme.space[3]}px;
                right: auto;
            }
        }
        ${IconWrapper} {
            ${({ hasIconRight }) =>
                hasIconRight &&
                css`
                    margin-left: 0;
                    margin-right: 15px;
                `};
        }
    }
`;

/* BUTTON TYPES */

export type PrimaryButtonType = { disabled?: boolean & TypographyProps & WidthProps };

export const Primary = styled(Button)<PrimaryButtonType>`
    color: ${({ disabled }) => (disabled ? theme.colors.platinumMedium : theme.colors.white)};
    background-color: ${({ disabled }) => (disabled ? "transparent" : theme.colors.azure)};
    border-color: ${({ disabled }) => (disabled ? theme.colors.platinumMedium : theme.colors.azure)};
    &:visited {
        color: ${({ disabled }) => (disabled ? theme.colors.platinumMedium : theme.colors.white)};
    }
    &::after {
        background: ${theme.colors.white};
    }

    ${({ disabled }) =>
        disabled &&
        css`
            color: ${theme.colors.platinumLight} !important;
            background-color: transparent !important;
            &::after {
                display: none;
            }
        `};
`;

export const Dark = styled(Button)`
    display: flex;
    color: ${({ disabled }) => (disabled ? theme.colors.platinumMedium : theme.colors.white)};
    background-color: ${({ disabled }) => (disabled ? "transparent" : theme.colors.black)};
    border-color: ${({ disabled }) => (disabled ? theme.colors.platinumMedium : theme.colors.black)};
    ${IconAnimatedToggleArrowStyle} {
        position: absolute;
        top: 50%;
        right: ${theme.space[3]}px;
        transform: translateY(-50%);
        svg {
            top: 0;
        }
    }
    &::after {
        display: none;
    }

    html[dir="rtl"] & {
        ${IconAnimatedToggleArrowStyle} {
            left: ${theme.space[3]}px;
            right: auto;
        }
    }
`;

export const OutlinePrimary = styled(Button)`
    color: ${theme.colors.black};
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.black};
`;

export const Lightest = styled(Button)`
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.platinumLight};
`;

export const Negative = styled(Button)<ButtonType>`
    color: ${theme.colors.black};
    background-color: ${theme.colors.white};
`;

export const Light = styled(Button)`
    color: ${theme.colors.black};
    background-color: ${theme.colors.mist};
`;

/* BUTTON STYLED AS A LINK */

type LinkType = {
    underline?: boolean;
    negative?: boolean;
    hasIconRight?: boolean;
    inlineIcon?: boolean;
    position?: string;
};

export const Link = styled(Button)<LinkType>`
    min-width: 0;
    padding: 0;
    padding-left: 26px;
    font-family: ${theme.fonts.regular};
    font-size: 17px; /* lexus uses px values */
    text-align: start;
    text-transform: none;
    color: ${({ negative }) => (negative ? theme.colors.white : theme.colors.midnight)};
    background-color: transparent;
    border: none;
    vertical-align: middle;
    ${position};
    ${(props) =>
        props.underline &&
        css`
            text-decoration: underline;
        `};

    &::after {
        display: none;
    }
    &:disabled {
        text-decoration: none;
        background-color: transparent !important;
        opacity: 0.5;
    }

    html[dir="rtl"] & {
        padding-left: 0;
        padding-right: 26px;
    }
`;

/* REMOVE BUTTON BASED ON BUTTON.LINK */

export const Remove = styled(Link)`
    display: flex;
    justify-content: left;
    margin-top: 10px;
    padding: 13px;
    padding-left: 26px;
    color: ${theme.colors.black};
    &::after {
        display: none;
    }
    svg {
        display: none;
    }

    html[dir="rtl"] & {
        padding-left: 0;
        padding-right: 26px;
    }
`;

/* CTA ICON WRAPPER */

export const CtaIconWrapper = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    margin-right: 10px;
    color: ${theme.colors.white};
    background-color: ${theme.colors.black};
    border-radius: 50%;

    html[dir="rtl"] & {
        margin-left: 10px;
        margin-right: 0;
    }
`;

export const EllipsisText = styled.span`
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

export default Button;
